import { Box, Typography, Paper, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, FormControl, InputLabel, Select, MenuItem, Chip, Avatar } from "@mui/material";
import { useState, useMemo } from "react";
import { useGetRafflesQuery, useGetAccountsQuery } from "../../store/api";
import { IRaffle } from "../../store/interfaces/IRaffle";
import { useAppSelector } from "../../store/hooks/useAppSelector";
import { RootState } from "../../store/types/rootState";
import { DollarSign, TrendingUp, BarChart3 } from "lucide-react";

interface BillingRaffle {
  raffleId: string;
  raffleName: string;
  accountId: string;
  accountName: string;
  isChilean: boolean;
  accountCreatedOrder: number;
  price: number;
  currency: 'USD' | 'CLP';
  operatorFee: number;
  adminFee: number;
  createdDate: string;
}

interface BillingSummary {
  totalBilled: number;
  totalOperator: number;
  totalAdmin: number;
  currency: 'USD' | 'CLP';
  raffleCount: number;
}

export const ReporteFacturacion = () => {
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date();
    // Si estamos antes de noviembre, mostrar noviembre del año anterior
    if (now.getMonth() < 10) { // 0-9 = enero a octubre, 10 = noviembre
      return `${now.getFullYear() - 1}-11`;
    }
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const { data: raffles = [] } = useGetRafflesQuery(undefined);
  const { data: accounts = [] } = useGetAccountsQuery(undefined);

  // Crear mapa de cuentas para acceso rápido, ordenadas por fecha de creación
  const accountsMap = useMemo(() => {
    // Ordenar cuentas por fecha de creación (si existe createdAt) o por orden de llegada
    const sortedAccounts = [...accounts].sort((a, b) => {
      // Si tienen createdAt, ordenar por fecha
      const aDate = (a as any).createdAt ? new Date((a as any).createdAt).getTime() : 0;
      const bDate = (b as any).createdAt ? new Date((b as any).createdAt).getTime() : 0;
      return aDate - bDate;
    });

    const map = new Map();
    for (let i = 0; i < sortedAccounts.length; i++) {
      map.set(sortedAccounts[i].id, {
        ...sortedAccounts[i],
        createdOrder: i + 1, // Orden de creación (1-based)
      });
    }
    return map;
  }, [accounts]);

  // Obtener número de soporte WhatsApp de una cuenta
  const getSupportWhatsapp = (accountId: string): string => {
    const account = accountsMap.get(accountId);
    if (!account) return '';
    
    const supportWhatsapp = account.socialMediaList?.find(
      (sm: any) => sm.name === 'supportWhatsapp'
    );
    
    if (!supportWhatsapp) return '';
    
    // Extraer el prefijo del número (ej: "+56" de "+56912345678")
    const username = supportWhatsapp.username || '';
    const match = username.match(/^(\+\d{1,3})/);
    return match ? match[1] : '';
  };

  // Determinar si una cuenta es chilena
  const isChileanAccount = (accountId: string): boolean => {
    return getSupportWhatsapp(accountId) === '+56';
  };

  const userLoggedId = useAppSelector(
    ({ user }: RootState) => user.accountId,
  );

  // Calcular precio de facturación para una rifa
  const calculateRafflePrice = (raffle: IRaffle, accountCreatedOrder: number): { price: number; currency: 'USD' | 'CLP' } => {
    const isChilean = isChileanAccount(raffle.accountId);
    
    if (isChilean) {
      return { price: 250000, currency: 'CLP' };
    }
    
    // Para cuentas no chilenas, verificar fecha de creación
    const account = accountsMap.get(raffle.accountId);
    
    // Si no tiene createdAt o createdAt es menor a 5 de noviembre: $250 USD
    // Si tiene createdAt y es después del 5 de noviembre: $200 USD
    if (account?.createdAt) {
      const accountCreatedAt = new Date(account.createdAt);
      
      // Verificar que la fecha sea válida
      if (!Number.isNaN(accountCreatedAt.getTime())) {
        // Fecha límite: 5 de noviembre del mismo año a las 23:59:59
        const cutoffDate = new Date(accountCreatedAt.getFullYear(), 10, 5, 23, 59, 59); // 10 = noviembre (0-indexed)
        
        // Si se creó después del 5 de noviembre (después de las 23:59:59 del 5 de noviembre)
        if (accountCreatedAt.getTime() > cutoffDate.getTime()) {
          return { price: 200, currency: 'USD' };
        }
      }
    }
    
    // Por defecto: $250 USD (si no tiene createdAt o se creó antes o igual al 5 de noviembre)
    return { price: 250, currency: 'USD' };
  };

  // Filtrar y procesar rifas del mes seleccionado
  const billingData = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    
    // Filtrar rifas del mes y excluir oscar-alarcon
    const monthRaffles = raffles.filter((raffle) => {
      if (raffle.accountId === 'oscar-alarcon' || raffle.accountId === 'agudojhonny' || raffle.accountId === 'carlos-pinango') return false;

      if (raffle.sorterId !== "77b1c2e7-c66e-4229-aca1-518f45858e55") return false;
      
      const raffleDate = new Date(raffle.createdDate);
      return raffleDate.getFullYear() === year && raffleDate.getMonth() + 1 === month;
    });

    // Procesar cada rifa
    const billingRaffles: BillingRaffle[] = monthRaffles.map((raffle) => {
      const account = accountsMap.get(raffle.accountId);
      const accountCreatedOrder = account?.createdOrder || 0;
      const isChilean = isChileanAccount(raffle.accountId);
      const { price, currency } = calculateRafflePrice(raffle, accountCreatedOrder);
      
      let operatorFee: number;
      let adminFee: number;
      
      if (isChilean) {
        operatorFee = 50000; // CLP
        adminFee = 200000; // CLP
      } else {
        operatorFee = 50; // USD
        adminFee = price - operatorFee; // USD
      }
      
      return {
        raffleId: raffle.id,
        raffleName: raffle.name,
        accountId: raffle.accountId,
        accountName: account?.name || raffle.accountId,
        isChilean,
        accountCreatedOrder,
        price,
        currency,
        operatorFee,
        adminFee,
        createdDate: raffle.createdDate,
      };
    });

    // Calcular resumen (ya no se usa, pero lo dejamos por si acaso)
    const summary: BillingSummary = billingRaffles.reduce(
      (acc, raffle) => {
        acc.totalBilled += raffle.price;
        acc.totalOperator += raffle.operatorFee;
        acc.totalAdmin += raffle.adminFee;
        acc.raffleCount += 1;
        
        // Determinar moneda principal (si hay mezcla, usar USD)
        if (billingRaffles.length > 0) {
          const hasCLP = billingRaffles.some(r => r.currency === 'CLP');
          const hasUSD = billingRaffles.some(r => r.currency === 'USD');
          
          if (hasCLP && hasUSD) {
            acc.currency = 'USD'; // Por defecto si hay mezcla
          } else if (hasCLP) {
            acc.currency = 'CLP';
          } else {
            acc.currency = 'USD';
          }
        } else {
          acc.currency = 'USD'; // Por defecto
        }
        
        return acc;
      },
      { totalBilled: 0, totalOperator: 0, totalAdmin: 0, currency: 'USD' as 'USD' | 'CLP', raffleCount: 0 }
    );

    return { raffles: billingRaffles, summary };
  }, [raffles, accountsMap, selectedMonth]);

  // Generar opciones de meses (solo noviembre en adelante)
  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 1-12
    
    // Empezar desde noviembre del año actual o del año anterior
    let startYear = currentYear;
    let startMonth = 11; // Noviembre
    
    // Si estamos antes de noviembre, mostrar desde noviembre del año anterior
    if (currentMonth < 11) {
      startYear = currentYear - 1;
    }
    
    // Generar meses desde noviembre hasta el mes actual (o el último mes disponible)
    let year = startYear;
    let month = startMonth;
    
    while (year < currentYear || (year === currentYear && month <= currentMonth)) {
      const date = new Date(year, month - 1, 1);
      const value = `${year}-${String(month).padStart(2, '0')}`;
      const label = date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long' });
      options.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
      
      // Avanzar al siguiente mes
      month++;
      if (month > 12) {
        month = 1;
        year++;
      }
    }
    
    return options;
  }, []);

  // Separar totales por moneda
  const usdTotals = useMemo(() => {
    return billingData.raffles
      .filter(r => r.currency === 'USD')
      .reduce(
        (acc, r) => ({
          totalBilled: acc.totalBilled + r.price,
          totalOperator: acc.totalOperator + r.operatorFee,
          totalAdmin: acc.totalAdmin + r.adminFee,
          count: acc.count + 1,
        }),
        { totalBilled: 0, totalOperator: 0, totalAdmin: 0, count: 0 }
      );
  }, [billingData.raffles]);

  const clpTotals = useMemo(() => {
    return billingData.raffles
      .filter(r => r.currency === 'CLP')
      .reduce(
        (acc, r) => ({
          totalBilled: acc.totalBilled + r.price,
          totalOperator: acc.totalOperator + r.operatorFee,
          totalAdmin: acc.totalAdmin + r.adminFee,
          count: acc.count + 1,
        }),
        { totalBilled: 0, totalOperator: 0, totalAdmin: 0, count: 0 }
      );
  }, [billingData.raffles]);

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Header con selector de mes */}
        <Grid item xs={12}>
          <Paper 
            elevation={2}
            sx={{ 
              p: 3.5, 
              borderRadius: 3,
              border: '1px solid rgba(0,0,0,0.08)',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography 
                variant="h4" 
                fontWeight="bold"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Reporte de Facturación
              </Typography>
              <FormControl variant="outlined" sx={{ minWidth: 250 }}>
                <InputLabel>Mes</InputLabel>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  label="Mes"
                >
                  {monthOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>
        </Grid>

        {/* Dashboard con totales */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {/* Totales USD */}
            {usdTotals.count > 0 && (
              <>
                <Grid item xs={12} md={4}>
                  <Paper 
                    elevation={6}
                    sx={{ 
                      p: 3.5, 
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 8,
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.25)',
                          width: 56,
                          height: 56,
                          backdropFilter: 'blur(10px)',
                        }}
                        variant="rounded"
                      >
                        <DollarSign size={28} color="#fff" />
                      </Avatar>
                      <Box flex={1}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            opacity: 0.9,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Total Facturado
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" sx={{ mt: 0.5 }}>
                          ${usdTotals.totalBilled.toLocaleString('es-ES')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: '0.875rem' }}>
                          USD • {usdTotals.count} rifa{usdTotals.count !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper 
                    elevation={6}
                    sx={{ 
                      p: 3.5, 
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                      color: 'white',
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 8,
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.25)',
                          width: 56,
                          height: 56,
                          backdropFilter: 'blur(10px)',
                        }}
                        variant="rounded"
                      >
                        <TrendingUp size={28} color="#fff" />
                      </Avatar>
                      <Box flex={1}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            opacity: 0.9,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Total Operador
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" sx={{ mt: 0.5 }}>
                          ${usdTotals.totalOperator.toLocaleString('es-ES')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: '0.875rem' }}>
                          USD • $50 por rifa
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper 
                    elevation={6}
                    sx={{ 
                      p: 3.5, 
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
                      color: 'white',
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 8,
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.25)',
                          width: 56,
                          height: 56,
                          backdropFilter: 'blur(10px)',
                        }}
                        variant="rounded"
                      >
                        <BarChart3 size={28} color="#fff" />
                      </Avatar>
                      <Box flex={1}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            opacity: 0.9,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Total Admin
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" sx={{ mt: 0.5 }}>
                          ${usdTotals.totalAdmin.toLocaleString('es-ES')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: '0.875rem' }}>
                          USD • ${usdTotals.count > 0 ? (usdTotals.totalAdmin / usdTotals.count).toFixed(0) : 0} promedio
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </>
            )}

            {/* Totales CLP */}
            {clpTotals.count > 0 && (
              <>
                <Grid item xs={12} md={4}>
                  <Paper 
                    elevation={6}
                    sx={{ 
                      p: 3.5, 
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      color: 'white',
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 8,
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.25)',
                          width: 56,
                          height: 56,
                          backdropFilter: 'blur(10px)',
                        }}
                        variant="rounded"
                      >
                        <DollarSign size={28} color="#fff" />
                      </Avatar>
                      <Box flex={1}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            opacity: 0.9,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Total Facturado
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" sx={{ mt: 0.5 }}>
                          ${clpTotals.totalBilled.toLocaleString('es-ES')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: '0.875rem' }}>
                          CLP • {clpTotals.count} rifa{clpTotals.count !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper 
                    elevation={6}
                    sx={{ 
                      p: 3.5, 
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      color: 'white',
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 8,
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.25)',
                          width: 56,
                          height: 56,
                          backdropFilter: 'blur(10px)',
                        }}
                        variant="rounded"
                      >
                        <TrendingUp size={28} color="#fff" />
                      </Avatar>
                      <Box flex={1}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            opacity: 0.9,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Total Operador
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" sx={{ mt: 0.5 }}>
                          ${clpTotals.totalOperator.toLocaleString('es-ES')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: '0.875rem' }}>
                          CLP • $50.000 por rifa
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper 
                    elevation={6}
                    sx={{ 
                      p: 3.5, 
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                      color: 'white',
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 8,
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.25)',
                          width: 56,
                          height: 56,
                          backdropFilter: 'blur(10px)',
                        }}
                        variant="rounded"
                      >
                        <BarChart3 size={28} color="#fff" />
                      </Avatar>
                      <Box flex={1}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            opacity: 0.9,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Total Admin
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" sx={{ mt: 0.5 }}>
                          ${clpTotals.totalAdmin.toLocaleString('es-ES')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: '0.875rem' }}>
                          CLP • $200.000 por rifa
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </>
            )}

            {/* Mensaje si no hay datos */}
            {billingData.raffles.length === 0 && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No hay rifas facturadas para el mes seleccionado.
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Tabla de rifas */}
        {billingData.raffles.length > 0 && (
          <Grid item xs={12}>
            <Paper 
              elevation={2}
              sx={{ 
                p: 3, 
                borderRadius: 3,
                border: '1px solid rgba(0,0,0,0.08)',
              }}
            >
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
                Detalle de Rifas Facturadas
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Rifa</strong></TableCell>
                      <TableCell><strong>Cuenta</strong></TableCell>
                      <TableCell><strong>País</strong></TableCell>
                      <TableCell><strong>Orden Creación</strong></TableCell>
                      <TableCell align="right"><strong>Precio</strong></TableCell>
                      <TableCell align="right"><strong>Operador</strong></TableCell>
                      <TableCell align="right"><strong>Admin</strong></TableCell>
                      <TableCell><strong>Fecha</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billingData.raffles.map((raffle) => (
                      <TableRow key={raffle.raffleId}>
                        <TableCell>{raffle.raffleName}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {raffle.accountName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {raffle.accountId}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={raffle.isChilean ? 'Chile' : 'Internacional'}
                            color={raffle.isChilean ? 'primary' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{raffle.accountCreatedOrder}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="medium">
                            {raffle.currency === 'USD' ? '$' : ''}
                            {raffle.price.toLocaleString('es-ES')}
                            {raffle.currency === 'CLP' ? ' CLP' : ' USD'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="success.main">
                            {raffle.currency === 'USD' ? '$' : ''}
                            {raffle.operatorFee.toLocaleString('es-ES')}
                            {raffle.currency === 'CLP' ? ' CLP' : ' USD'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="info.main">
                            {raffle.currency === 'USD' ? '$' : ''}
                            {raffle.adminFee.toLocaleString('es-ES')}
                            {raffle.currency === 'CLP' ? ' CLP' : ' USD'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(raffle.createdDate).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
